import { defineComponent as _defineComponent } from 'vue'
import { computed, ComputedRef, useSlots } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import NotificationDot from '@/components/NotificationDot.vue';
import TextWithHighlightedTerm from '@/components/search/TextWithHighlightedTerm.vue';
import UserIdent from '@/components/UserIdent.vue';
import { TextWithHighlight } from '@/data/datatypes/TextWithHighlight';
import { Track } from '@/data/datatypes/Track';
import { LimitedUserDetails } from '@/data/datatypes/UserDetails';
import { UserStatus } from '@/data/datatypes/UserStatus';
import { getChatName } from '@/data/helpers/ChatHelper';
import { getRecentActivityDate } from '@/data/helpers/DateHelper';
import { formatNotificationCount } from '@/data/helpers/TrackHelper';
import { useUserStore } from '@/stores/User';
import UserStatusIcon from '@/UserStatusIcon.vue';

interface Props {
  to: RouteLocationRaw;
  name?: string | null;
  subtext?: string | null;
  // e.g. user status for direct chat
  online?: boolean | null;
  // e.g. new message
  numNotifications?: number | null;
  displayDate?: number | null;
  date?: Date | null;
  // the userID of the user whose status we want to display
  userStatusUserId?: string | null;
  // This will override the default selection highlighting based on the route specified in the 'to' prop
  selected?: boolean | null;
  track?: Track | null;
  user?: LimitedUserDetails | null;
  hideIdent?: boolean;
  highlightRegex?: RegExp;
  textWithHighlights?: TextWithHighlight[];
}

interface Emits {
  (e: 'click', event: MouseEvent): void,
}

export default /*#__PURE__*/_defineComponent({
  __name: 'NavListItem',
  props: {
    to: null,
    name: { default: null },
    subtext: { default: null },
    online: { type: [Boolean, null], default: null },
    numNotifications: { default: null },
    displayDate: { default: null },
    date: { default: null },
    userStatusUserId: { default: null },
    selected: { type: [Boolean, null], default: null },
    track: { default: null },
    user: { default: null },
    hideIdent: { type: Boolean, default: false },
    highlightRegex: null,
    textWithHighlights: null
  },
  emits: ["click"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'click', event: MouseEvent): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { to: RouteLocationRaw, name: string | null, subtext: string | null, online: boolean | null, numNotifications: number | null, displayDate: number | null, date: Date | null, userStatusUserId: string | null, selected: boolean | null, track: Track | null, user: LimitedUserDetails | null, hideIdent: boolean, highlightRegex?: RegExp, textWithHighlights?: TextWithHighlight[] }

const userStore = useUserStore();

const slots = useSlots();





const currentUserId: ComputedRef<string> = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return userStore.currentUserId!;
});

const showIdent: ComputedRef<boolean> = computed(() => {
  return !props.hideIdent && (isChat.value || isUser.value || !!props.name);
});

const linkSelected: ComputedRef<string | null> = computed(() => {
  // If 'selected' hasn't been specified, then let it default to the route matching for the 'to' prop
  if (props.selected === undefined || props.selected === null) {
    return null;
  }

  return props.selected ? 'selected' : 'notSelected';
});

const isChat: ComputedRef<boolean> = computed(() => {
  if (props.track) {
    return isPrivateChat(props.track);
  }
  return false;
});

const isUser: ComputedRef<boolean> = computed(() => {
  return !!props.user;
});

const isGroupChat: ComputedRef<boolean> = computed(() => {
  let groupChat: boolean = false;
  if (isChat.value && props.track?.typeMetadata) {
    const commaIndex = props.track.typeMetadata.indexOf(',');
    groupChat = !!(commaIndex && commaIndex !== props.track.typeMetadata.lastIndexOf(','));
  }
  return groupChat;
});

const userStatusForUser: ComputedRef<UserStatus | null> = computed(() => {
  if (props.userStatusUserId) {
    return userStore.userStatusesForUsers.get(props.userStatusUserId) ?? null;
  }
  return null;
});

const isNotificationsSlotPopulated: ComputedRef<boolean> = computed(() => {
  return !!slots.notification;
});

const formattedName: ComputedRef<string> = computed(() => {
  if (props.name) {
    return props.name;
  }
  if (props.track) {
    return getChatName(props.track, currentUserId.value, userStore.displayDetailsForUsers);
  }
  return '';
});

const formattedDate: ComputedRef<string | null> = computed(() => {
  const parsedDate: Date | null = props.displayDate ? new Date(props.displayDate) : props.date;
  if (!parsedDate) {
    return null;
  }

  return getRecentActivityDate(parsedDate);
});

const userIdent: ComputedRef<LimitedUserDetails | null> = computed(() => {
  if (props.user) {
    return props.user;
  }
  if (!isChat.value) {
    return null;
  }
  if (privateChatUsers.value.length) {
    return privateChatUsers.value[0];
  }
  return null;
});

const privateChatUsers: ComputedRef<LimitedUserDetails[]> = computed(() => {
  if (props.track) {
    return getPrivateChatUsers(props.track, currentUserId.value, userStore.displayDetailsForUsers);
  }
  return [];
});

const displayNumber: ComputedRef<number | undefined> = computed(() => {
  if (isGroupChat.value) {
    return privateChatUsers.value.length;
  }
  return undefined;
});

function handleClick(event: MouseEvent): MouseEvent {
  emit('click', event);
  return event;
}

function formatNumMessages(num: number): string | null {
  return formatNotificationCount(num);
}

return { __sfc: true,userStore, slots, props, emit, currentUserId, showIdent, linkSelected, isChat, isUser, isGroupChat, userStatusForUser, isNotificationsSlotPopulated, formattedName, formattedDate, userIdent, privateChatUsers, displayNumber, handleClick, formatNumMessages, NotificationDot, TextWithHighlightedTerm, UserIdent, UserStatusIcon }
}

})